// Schema-completion client for SQL-editor autocomplete. Fetches a connection's
// cached schema (all accessible databases) from the backend, caches it per
// connection in a shared signal (so many editor tabs share ONE fetch), and
// builds the CodeMirror lang-sql `schema` namespace from it.
//
// Freshness: the backend serves cached rows immediately (may be `building` on a
// first populate, or carry stale rows it re-pulls in the background). We poll
// while `building`. A Ninocat-side schema change calls refreshCompletionSchema.
import { createSignal, createEffect, createMemo } from 'solid-js';
import { api } from './api.js';

// connId -> { get, set, loading, polling }
const cache = new Map();

function entry(connId) {
  let e = cache.get(connId);
  if (!e) {
    const [get, set] = createSignal({ databases: [], building: false, loaded: false });
    e = { get, set, loading: false, polling: false };
    cache.set(connId, e);
  }
  return e;
}

// Reactive accessor for a connection's completion state ({databases, building,
// loaded}). Components read this to react to the schema arriving.
export function completionState(connId) { return entry(connId).get; }

async function fetchInto(connId) {
  const e = entry(connId);
  const r = await api.get(`/api/connections/${connId}/completion`);
  e.set({ databases: r.databases || [], building: !!r.building, loaded: true });
  if (e.get().building) poll(connId);
}

function poll(connId) {
  const e = entry(connId);
  if (e.polling) return;
  e.polling = true;
  const tick = async () => {
    try { await fetchIntoQuiet(connId); } catch { /* keep last */ }
    if (entry(connId).get().building) setTimeout(tick, 2500);
    else entry(connId).polling = false;
  };
  setTimeout(tick, 2500);
}
async function fetchIntoQuiet(connId) {
  const e = entry(connId);
  const r = await api.get(`/api/connections/${connId}/completion`);
  e.set({ databases: r.databases || [], building: !!r.building, loaded: true });
}

// Load (once) a connection's completion schema. force re-fetches even if loaded.
export async function loadCompletionSchema(connId, { force = false } = {}) {
  if (!connId) return;
  const e = entry(connId);
  if (!force && (e.get().loaded || e.loading)) return;
  e.loading = true;
  try { await fetchInto(connId); }
  catch { /* leave previous state */ }
  finally { e.loading = false; }
}

// Refresh the cache from MySQL: one database (fast, default) or the whole
// connection (background). Used by the Refresh button + after a Ninocat DDL.
export async function refreshCompletionSchema(connId, { database = '', all = false } = {}) {
  if (!connId) return;
  const e = entry(connId);
  const qs = all ? '?all=1' : (database ? `?database=${encodeURIComponent(database)}` : '');
  try {
    const r = await api.post(`/api/connections/${connId}/completion/refresh${qs}`, {});
    if (all || !database) {
      e.set({ ...e.get(), building: true });
      poll(connId);
    } else {
      // Merge the returned single-database payload into the cached set.
      const incoming = r.databases || [];
      if (incoming.length) {
        const next = e.get().databases.slice();
        for (const nd of incoming) {
          const i = next.findIndex((d) => d.database === nd.database);
          if (i >= 0) next[i] = nd; else next.push(nd);
        }
        e.set({ ...e.get(), databases: next, loaded: true });
      }
    }
  } catch { /* ignore — keep showing the last good schema */ }
}

// sqlSchemaFor — a reactive `schema` accessor for <CodeMirrorSQL schema={…}>
// from a connection-id accessor and a database accessor: loads the
// connection's completion cache (shared, fetched once) and follows changes of
// either. Every SQL editor that knows its connection should pass one, or it
// gets keyword-only autocomplete.
export function sqlSchemaFor(connId, database) {
  const id = () => { const v = connId(); return v ? Number(v) || v : null; };
  createEffect(() => { if (id()) loadCompletionSchema(id()); });
  return createMemo(() => {
    if (!id()) return null;
    const st = completionState(id())();
    const db = database() || undefined;
    return { schema: buildSqlSchema(st.databases), defaultSchema: db, routines: buildRoutineList(st.databases, db) };
  });
}

// keyColumnsFor — for the "Lax — key column" safe-update tier. Returns the
// set of lower-cased column names that participate in ANY index on the given
// table (PK, unique, or secondary) — derived from the cached schema, NO
// extra round-trip to MySQL. Returns null when the answer is "unknown":
// either the cache has no row for this (db, table), or the row is from a
// cache built before the Keys field existed. The caller decides what to do
// with null — typically: surface a "refresh schema" hint rather than block.
export function keyColumnsFor(databases, defaultDB, tableName) {
  if (!defaultDB || !tableName) return null;
  const dbRow = (databases || []).find((d) => d.database === defaultDB);
  if (!dbRow) return null;
  const t = (dbRow.tables || []).find((x) => x.name === tableName) ||
            (dbRow.views || []).find((x) => x.name === tableName);
  if (!t) return null;
  if (!Array.isArray(t.keys)) return null; // pre-keys cache (server hasn't been refreshed since the schema bump)
  return new Set(t.keys.map((s) => s.toLowerCase()));
}

// Build the routine (function/procedure) completion list for the CURRENT
// database only — cross-DB routines aren't offered unqualified (they flood the
// list and the current DB is the priority). Each entry carries a snippet
// template `name(${p1}, ${p2})` (param names as tab-stops), a compact signature
// for the popup detail, and a longer info line.
export function buildRoutineList(databases, defaultSchema) {
  const out = [];
  const safe = (n, i) => (n && /^[A-Za-z0-9_]+$/.test(n) ? n : 'arg' + (i + 1));
  for (const db of databases || []) {
    if (db.database !== defaultSchema) continue;
    for (const r of (db.routines || [])) {
      const callName = r.name;
      const params = r.params || [];
      const snippet = `${callName}(${params.map((p, i) => '${' + safe(p.name, i) + '}').join(', ')})`;
      const sig = params.map((p) =>
        `${p.mode && p.mode !== 'IN' ? p.mode + ' ' : ''}${p.name || ''}${p.type ? ' ' + p.type : ''}`.trim()
      ).join(', ');
      out.push({
        label: callName,
        snippet,
        kind: r.kind, // 'FUNCTION' | 'PROCEDURE'
        detail: r.kind === 'FUNCTION' ? `(${sig}) → ${r.returns || '?'}` : `(${sig})`,
        info: `${r.kind === 'FUNCTION' ? 'function' : 'procedure'} ${callName}(${sig})` +
          (r.kind === 'FUNCTION' && r.returns ? ` RETURNS ${r.returns}` : ''),
      });
    }
  }
  return out;
}

// Length-preserving mask of SQL: string/comment bodies → spaces, so a backward
// scan can't be fooled by a keyword or paren inside a literal/comment.
function maskSql(s) {
  const a = s.split('');
  let inS = false, inD = false, inB = false, inLine = false, inBlock = false;
  for (let i = 0; i < a.length; i++) {
    const c = a[i], n = a[i + 1];
    if (inLine) { if (c === '\n') inLine = false; else a[i] = ' '; continue; }
    if (inBlock) { if (c === '*' && n === '/') { a[i] = ' '; a[i + 1] = ' '; i++; inBlock = false; } else a[i] = ' '; continue; }
    if (inS) { a[i] = ' '; if (c === '\\') { a[i + 1] = ' '; i++; } else if (c === "'") inS = false; continue; }
    if (inD) { a[i] = ' '; if (c === '\\') { a[i + 1] = ' '; i++; } else if (c === '"') inD = false; continue; }
    if (inB) { a[i] = ' '; if (c === '`') inB = false; continue; }
    if (c === '-' && n === '-') { inLine = true; a[i] = ' '; a[i + 1] = ' '; i++; continue; }
    if (c === '#') { inLine = true; a[i] = ' '; continue; }
    if (c === '/' && n === '*') { inBlock = true; a[i] = ' '; a[i + 1] = ' '; i++; continue; }
    if (c === "'") { inS = true; a[i] = ' '; continue; }
    if (c === '"') { inD = true; a[i] = ' '; continue; }
    if (c === '`') { inB = true; a[i] = ' '; continue; }
  }
  return a.join('');
}

const RTN_TABLE_KW = new Set(['from', 'join', 'inner', 'left', 'right', 'cross', 'natural', 'straight_join', 'into', 'update']);
const RTN_EXPR_KW = new Set([
  'select', 'where', 'having', 'on', 'set', 'values', 'value', 'by', 'group', 'order',
  'when', 'then', 'else', 'return', 'returning', 'and', 'or', 'not', 'like', 'as',
  'using', 'case', 'limit', 'between', 'in', 'if',
]);
const RTN_IDENT = /[A-Za-z0-9_$]/;

// Decide which routine completions fit the position just before the cursor:
//   'procedure' — right after CALL
//   'function'  — an expression position (SELECT list, WHERE, function args, …)
//   'none'      — a table position (after FROM/JOIN/INTO/UPDATE) → no routines
// Heuristic backward scan over the masked text (see the file's design note).
export function routineCompletionMode(textBefore) {
  const s = maskSql(String(textBefore || ''));
  let i = s.length;
  while (i > 0 && (RTN_IDENT.test(s[i - 1]) || s[i - 1] === '.')) i--; // skip the partial word
  let depth = 0;
  const wordBefore = (end) => {
    let k = end;
    while (k > 0 && /\s/.test(s[k - 1])) k--;
    let we = k;
    while (k > 0 && RTN_IDENT.test(s[k - 1])) k--;
    return { word: s.slice(k, we).toLowerCase(), start: k };
  };
  while (i > 0) {
    while (i > 0 && /\s/.test(s[i - 1])) i--;
    if (i === 0) break;
    const c = s[i - 1];
    if (c === ')') { depth++; i--; continue; }
    if (c === '(') {
      if (depth > 0) { depth--; i--; continue; }
      const { word, start } = wordBefore(i - 1); // token before this unmatched '('
      if (word === '') { i = start; continue; } // grouping/subquery → scan outward
      if (RTN_TABLE_KW.has(word)) return 'none'; // derived-table subquery → expect a table/SELECT
      return 'function'; // function-call args, IN(…), VALUES(…), CALL args, etc.
    }
    if (RTN_IDENT.test(c)) {
      const { word, start } = wordBefore(i);
      if (word === 'call') return 'procedure';
      if (RTN_TABLE_KW.has(word)) return 'none';
      if (RTN_EXPR_KW.has(word)) return 'function';
      i = start; // alias / identifier — keep scanning
      continue;
    }
    i--; // operator / comma / etc.
  }
  return 'function'; // default: expression context
}

// Tokenize SQL into identifier / punctuation tokens with paren depth, skipping
// string + comment bodies and unwrapping `backtick` identifiers. Used by the
// FROM-clause table extractor.
function sqlTokens(s) {
  const toks = [];
  let depth = 0, i = 0;
  const n = s.length;
  while (i < n) {
    const c = s[i], nx = s[i + 1];
    if (/\s/.test(c)) { i++; continue; }
    if (c === '-' && nx === '-') { while (i < n && s[i] !== '\n') i++; continue; }
    if (c === '#') { while (i < n && s[i] !== '\n') i++; continue; }
    if (c === '/' && nx === '*') { i += 2; while (i < n && !(s[i] === '*' && s[i + 1] === '/')) i++; i += 2; continue; }
    if (c === "'" || c === '"') { const q = c; i++; while (i < n) { if (s[i] === '\\') { i += 2; continue; } if (s[i] === q) { i++; break; } i++; } continue; }
    if (c === '`') { i++; let name = ''; while (i < n) { if (s[i] === '`') { if (s[i + 1] === '`') { name += '`'; i += 2; continue; } i++; break; } name += s[i]; i++; } toks.push({ t: 'id', v: name, depth }); continue; }
    if (c === '(') { toks.push({ t: '(', depth }); depth++; i++; continue; }
    if (c === ')') { depth = Math.max(0, depth - 1); toks.push({ t: ')', depth }); i++; continue; }
    if (c === ',') { toks.push({ t: ',', depth }); i++; continue; }
    if (c === '.') { toks.push({ t: '.', depth }); i++; continue; }
    if (/[A-Za-z0-9_$]/.test(c)) { let j = i; while (j < n && /[A-Za-z0-9_$]/.test(s[j])) j++; toks.push({ t: 'id', v: s.slice(i, j), depth }); i = j; continue; }
    i++; // operators etc.
  }
  return toks;
}

const FROM_CLAUSE_END = new Set(['where', 'group', 'having', 'order', 'limit', 'union', 'procedure', 'into', 'for', 'lock', 'window', 'set', 'on', 'using']);
const FROM_JOIN_KW = new Set(['join', 'inner', 'left', 'right', 'cross', 'natural', 'straight_join', 'full', 'outer']);

// readRef parses one table reference at token index k: `[db.]table [[AS] alias]`.
// A derived-table subquery `( … ) [alias]` is skipped (no columns). Returns the
// ref (or null) + the next token index.
function readRef(toks, k) {
  const t = toks[k];
  if (!t) return null;
  if (t.t === '(') {
    let depth = 1, j = k + 1;
    while (j < toks.length && depth > 0) { if (toks[j].t === '(') depth++; else if (toks[j].t === ')') depth--; j++; }
    if (toks[j] && toks[j].t === 'id' && toks[j].v.toLowerCase() === 'as') j++;
    if (toks[j] && toks[j].t === 'id') j++;
    return { ref: null, next: j };
  }
  if (t.t !== 'id') return null;
  const lw = t.v.toLowerCase();
  if (FROM_CLAUSE_END.has(lw) || FROM_JOIN_KW.has(lw)) return null;
  let db = null, table = t.v, j = k + 1;
  if (toks[j] && toks[j].t === '.' && toks[j + 1] && toks[j + 1].t === 'id') { db = table; table = toks[j + 1].v; j += 2; }
  let alias = null;
  if (toks[j] && toks[j].t === 'id' && toks[j].v.toLowerCase() === 'as') {
    if (toks[j + 1] && toks[j + 1].t === 'id') { alias = toks[j + 1].v; j += 2; }
  } else if (toks[j] && toks[j].t === 'id') {
    const av = toks[j].v.toLowerCase();
    if (!FROM_CLAUSE_END.has(av) && !FROM_JOIN_KW.has(av)) { alias = toks[j].v; j += 1; }
  }
  return { ref: { db, table, alias }, next: j };
}

// fromTables extracts the [{db, table, alias}] referenced in a statement's
// top-level FROM and JOIN clauses (so an expression-position completion can
// offer those tables' columns). Derived tables are skipped.
export function fromTables(statementText) {
  const toks = sqlTokens(String(statementText || ''));
  const out = [];
  for (let i = 0; i < toks.length; i++) {
    const tk = toks[i];
    if (tk.t !== 'id' || tk.depth !== 0) continue;
    const w = tk.v.toLowerCase();
    if (w === 'from') {
      let j = i + 1;
      while (j < toks.length) {
        const r = readRef(toks, j);
        if (!r) break;
        if (r.ref) out.push(r.ref);
        j = r.next;
        if (toks[j] && toks[j].t === ',' && toks[j].depth === 0) { j++; continue; }
        break;
      }
      i = j - 1;
    } else if (FROM_JOIN_KW.has(w)) {
      const r = readRef(toks, i + 1);
      if (r && r.ref) out.push(r.ref);
      if (r) i = r.next - 1;
    }
  }
  return out;
}

// aliasForTable finds the alias a statement gives a specific table (matched by
// name, case-insensitively; the db is compared only when both sides name one),
// or '' when the table is used bare (`FROM t`) or not found. For a self-join
// that aliases the same table twice, the FIRST occurrence's alias is returned.
// Powers the right-click "Expand columns of X" so the generated list carries the
// same `alias.` prefix the query already uses (e.g. `d.No_Bukti`).
export function aliasForTable(statementText, tableName, dbName) {
  const want = String(tableName || '').toLowerCase();
  if (!want) return '';
  for (const r of fromTables(statementText)) {
    if (!r.table || r.table.toLowerCase() !== want) continue;
    if (dbName && r.db && r.db.toLowerCase() !== String(dbName).toLowerCase()) continue;
    return r.alias || '';
  }
  return '';
}

// Modifiers between INSERT/REPLACE and the target table (and the optional INTO).
const INSERT_MODS = new Set(['into', 'ignore', 'low_priority', 'high_priority', 'delayed']);

// writeTargetTables extracts the table(s) a write statement targets where there
// is no FROM clause: INSERT/REPLACE [INTO] <table>, and UPDATE <table>[, <table>]
// (multi-table). This lets column completion work in the INSERT column list and
// the UPDATE SET clause. Complements fromTables (which handles FROM/JOIN, incl.
// DELETE … FROM and INSERT … SELECT … FROM).
export function writeTargetTables(statementText) {
  const toks = sqlTokens(String(statementText || ''));
  const out = [];
  for (let i = 0; i < toks.length; i++) {
    const tk = toks[i];
    if (tk.t !== 'id' || tk.depth !== 0) continue;
    const w = tk.v.toLowerCase();
    if (w === 'insert' || w === 'replace') {
      let j = i + 1;
      while (toks[j] && toks[j].t === 'id' && INSERT_MODS.has(toks[j].v.toLowerCase())) j++;
      const r = readRef(toks, j);
      if (r && r.ref) out.push(r.ref);
    } else if (w === 'update') {
      let j = i + 1;
      while (j < toks.length) {
        const r = readRef(toks, j);
        if (!r || !r.ref) break;
        out.push(r.ref);
        j = r.next;
        if (toks[j] && toks[j].t === ',' && toks[j].depth === 0) { j++; continue; }
        break;
      }
    }
  }
  return out;
}

// statementHasJoin reports whether a statement has a top-level JOIN keyword
// (string/comment-safe via sqlTokens). Used to decide table aliasing even before
// the joined table is typed (`FROM a JOIN ‸`).
export function statementHasJoin(statementText) {
  for (const tk of sqlTokens(String(statementText || ''))) {
    if (tk.t === 'id' && tk.depth === 0 && tk.v.toLowerCase() === 'join') return true;
  }
  return false;
}

// aliasContext decides, ONCE per completion, whether a table inserted into this
// statement's FROM/JOIN slot should be auto-aliased, and gathers the aliases /
// table names already in play so a derived alias can dodge them. We alias when a
// JOIN is present OR there are already ≥2 table refs (a lone first table stays
// bare). The partial word being completed counts as a ref, so two refs means
// "another table already there".
export function aliasContext(statementText) {
  const refs = fromTables(statementText);
  const shouldAlias = statementHasJoin(statementText) || refs.length >= 2;
  const taken = new Set();
  for (const r of refs) {
    if (r.alias) taken.add(r.alias.toLowerCase());
    if (r.table) taken.add(r.table.toLowerCase());
  }
  return { shouldAlias, taken };
}

// tableAlias derives a short, collision-free alias for a table name: strip a
// `tr_`/`tm_`/`t_` prefix, then take the initials of the underscore-separated
// words (`tr_jual_m`→`jm`, `tm_customer`→`c`, `tuser`→`t`). On collision with
// `taken` (a Set/array of lowercased aliases) append a digit (`jm`→`jm2`).
const ALIAS_PREFIX_RE = /^(?:tr|tm|t)_/;
export function tableAlias(tableName, taken) {
  const raw = String(tableName || '').toLowerCase();
  const stripped = raw.replace(ALIAS_PREFIX_RE, '') || raw;
  const words = stripped.split(/[^a-z0-9]+/).filter(Boolean);
  let base = words.map((w) => w[0]).join('');
  if (!base || !/^[a-z]/.test(base)) base = (stripped.match(/[a-z]/) || ['t'])[0];
  const used = taken instanceof Set ? taken : new Set(taken || []);
  if (!used.has(base)) return base;
  for (let i = 2; ; i++) { const cand = base + i; if (!used.has(cand)) return cand; }
}

// tableColumns resolves a table's (or view's) column list from the cached
// `databases` payload — used by the Ctrl+Shift+E column expander. Returns the
// column array or null if the table isn't in the cache.
export function tableColumns(databases, dbName, tableName) {
  for (const db of databases || []) {
    if (db.database !== dbName) continue;
    for (const t of (db.tables || [])) if (t.name === tableName) return t.columns || [];
    for (const v of (db.views || [])) if (v.name === tableName) return v.columns || [];
  }
  return null;
}

// findColumn looks up a column by name, case-insensitively, returning the
// canonical column object (with its real-case name) or null. The column expander
// uses it to validate an "Except" chip and reject unknown names.
export function findColumn(columns, name) {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return null;
  return (columns || []).find((c) => String(c.name).toLowerCase() === n) || null;
}

// expandColumns renders a comma-separated column list for the Ctrl+Shift+E
// expander: each column optionally prefixed (`alias.col`), minus any in `except`
// (matched case-insensitively). `prefix` empty ⇒ bare column names.
export function expandColumns(columns, except, prefix) {
  const ex = new Set((except || []).map((s) => String(s).toLowerCase()));
  const pfx = prefix ? prefix + '.' : '';
  return (columns || [])
    .filter((c) => !ex.has(String(c.name).toLowerCase()))
    .map((c) => pfx + c.name)
    .join(', ');
}

// Leading whitespace + SQL comments — so a statement keyword is still matched
// when preceded by `-- note` / `/* note */` / `# note`. Shared by the
// statement-kind detectors below (isSchemaChangingSQL, implicitCommitKind).
const LEAD = `(?:\\s|/\\*[\\s\\S]*?\\*/|--[^\\n]*\\n|#[^\\n]*\\n)*`;
const ic = (body) => new RegExp('^' + LEAD + body, 'i');

// Whether a statement changes schema (CREATE/ALTER/DROP/RENAME/TRUNCATE) — used
// to refresh autocomplete after a DDL runs. Skips leading whitespace/comments.
const DDL_RE = ic(`(?:create|alter|drop|rename|truncate)\\b`);
export function isSchemaChangingSQL(sqlText) { return DDL_RE.test(String(sqlText || '')); }

// MySQL "Statements That Cause an Implicit Commit" — the forms a user can
// realistically type into a query tab. Each one ends (commits) the current
// transaction *before* it runs, and that commit cannot be rolled back. The
// categories don't overlap in practice, so match order only decides the label.
// Note CREATE/DROP/RENAME USER fall under the DDL keyword match (fine — they do
// implicitly commit); only the non-DDL account verbs need their own entry.
const IMPLICIT_COMMIT = [
  { kind: 'DDL',         re: ic(`(?:create|alter|drop|rename|truncate)\\b`) },
  { kind: 'BEGIN',       re: ic(`(?:begin|start\\s+transaction)\\b`) },
  { kind: 'LOCK',        re: ic(`(?:un)?lock\\s+tables\\b`) },
  { kind: 'ACCOUNT',     re: ic(`(?:grant|revoke|set\\s+password)\\b`) },
  { kind: 'MAINTENANCE', re: ic(`(?:flush|reset|analyze|check|optimize|repair|cache\\s+index|load\\s+index)\\b`) },
  { kind: 'AUTOCOMMIT',  re: ic(`set\\s+autocommit\\s*=\\s*1\\b`) },
];

// implicitCommitKind returns a short label ('DDL', 'BEGIN', 'LOCK', …) when the
// statement would implicitly commit an open transaction, else null. Used to warn
// before running such a statement while a transaction is open.
export function implicitCommitKind(sqlText) {
  const s = String(sqlText || '');
  for (const { kind, re } of IMPLICIT_COMMIT) if (re.test(s)) return kind;
  return null;
}

// SQL DATA ACCESS values that mean "this function does not write data" — a read
// calling one is safe to run without a transaction. Everything else (MODIFIES SQL
// DATA, the unreliable CONTAINS SQL default, or unknown) is treated conservatively
// as a potential writer.
const READ_SAFE_ACCESS = new Set(['READS SQL DATA', 'NO SQL']);

// Set of stored-FUNCTION names (lowercased) that a read must keep a transaction
// for — i.e. every FUNCTION NOT explicitly flagged read-safe via its MySQL SQL
// DATA ACCESS characteristic. Procedures aren't callable from a SELECT, so only
// FUNCTIONs matter. Feeds isReadOnlySQL: a read that calls one of these might
// modify data, so the transaction is kept. Flagging a function READS SQL DATA /
// NO SQL (right-click, or the on-save prompt) removes it from this set and opts
// its callers into the tx-skip. Built-ins never appear here.
export function txGuardFunctionNames(databases) {
  const set = new Set();
  for (const db of databases || [])
    for (const r of (db.routines || []))
      if (r.kind === 'FUNCTION' && r.name && !READ_SAFE_ACCESS.has((r.dataAccess || '').toUpperCase()))
        set.add(r.name.toLowerCase());
  return set;
}

// True if the (already literal/comment-masked) SQL invokes any name in `fnNames`
// as a function call (`name(` — optional space, qualifier-agnostic so `db.fn(`
// still matches on `fn`). Built-ins never appear in fnNames, so ordinary reads
// using COUNT()/NOW()/etc. are not flagged.
const CALL_RE = /([A-Za-z0-9_$]+)\s*\(/g;
function callsKnownFunction(maskedSql, fnNames) {
  CALL_RE.lastIndex = 0;
  let m;
  while ((m = CALL_RE.exec(maskedSql)) !== null)
    if (fnNames.has(m[1].toLowerCase())) return true;
  return false;
}

// A statement that only READS — safe to run WITHOUT a wrapping transaction even
// when the Transaction toggle is on (there's nothing to commit, so a tx would
// just leave a dangling open snapshot). Deliberately CONSERVATIVE: matches only
// the unambiguous read leads, and bails on the locking / file-writing SELECT
// forms (FOR UPDATE / FOR SHARE / LOCK IN SHARE MODE want a tx to hold their
// locks; INTO OUTFILE/DUMPFILE write a file). Notably EXCLUDES a `WITH` lead —
// MySQL 8 allows `WITH … UPDATE/DELETE`, so a CTE isn't safe to assume read-only.
// Also bails if the read calls a function in `fnNames` (the tx-guard set from
// txGuardFunctionNames — every UDF not flagged read-safe), which could contain
// DML. Anything it doesn't recognise → false (tx is kept). UDF-call detection
// only works once the routine list has loaded (it's fetched for autocomplete);
// an unknown function isn't flagged.
const READ_LEAD_RE = ic(`(?:select|show|desc(?:ribe)?|explain|help|use)\\b`);
const NONREAD_SELECT_RE = /\b(?:for\s+update|for\s+share|lock\s+in\s+share\s+mode|into\s+(?:outfile|dumpfile))\b/i;
export function isReadOnlySQL(sqlText, fnNames) {
  const s = String(sqlText || '');
  if (!READ_LEAD_RE.test(s)) return false;
  const masked = maskSql(s);
  if (NONREAD_SELECT_RE.test(masked)) return false;
  if (fnNames && fnNames.size && callsKnownFunction(masked, fnNames)) return false;
  return true;
}

// Build the CodeMirror lang-sql `schema` namespace from the cached databases:
//   { dbName: { tableName: [col completions], … } }
// Each table/view MUST be a leaf array of its columns (NOT a {self,children}
// wrapper) — lang-sql only resolves a table to its columns for *unqualified*
// completion (e.g. `WHERE col…` against the FROM table) when the table is an
// array entry; a {self,children} value is treated as a sub-namespace (like a
// database) and its columns never surface unqualified. Columns carry their data
// type as the completion detail. defaultSchema=currentDb makes unqualified
// `table`/`table.col` resolve to the active DB while `otherdb.table.col` works.
export function buildSqlSchema(databases) {
  const ns = {};
  for (const db of databases || []) {
    const tables = {};
    const add = (t) => {
      tables[t.name] = (t.columns || []).map((c) => ({
        label: c.name, type: 'property', detail: c.type || undefined,
      }));
    };
    for (const t of (db.tables || [])) add(t);
    for (const v of (db.views || [])) add(v);
    ns[db.database] = tables;
  }
  return ns;
}
